import { cn } from '@/lib/cn';

export type KPITrend = 'up' | 'down' | 'stable';

export interface KPICardProps {
  value: string | number;
  label: string;
  trend?: KPITrend;
  trendPercent?: number;
  unit?: string;
  className?: string;
  onClick?: () => void;
  'aria-label'?: string;
}

const trendConfig: Record<KPITrend, { icon: string; color: string }> = {
  up: { icon: '↑', color: 'text-[#2e7d32]' },
  down: { icon: '↓', color: 'text-[#c62828]' },
  stable: { icon: '→', color: 'text-[#999]' },
};

export function KPICard({
  value,
  label,
  trend,
  trendPercent,
  unit,
  className,
  onClick,
  'aria-label': ariaLabel,
}: KPICardProps) {
  const surfaceClasses = cn(
    'rounded-[14px] border-[1.5px] border-[#f0f0f0] bg-white px-6 py-7 text-center shadow-[0_2px_12px_rgba(0,0,0,0.06)]',
    onClick &&
      'cursor-pointer transition-all duration-150 hover:-translate-y-[3px] hover:border-[#6B0F1A] hover:shadow-[0_8px_24px_rgba(0,0,0,0.1)]',
    className,
  );

  const content = (
    <>
      <p className="mb-1 text-[32px] font-extrabold text-[#1a1a1a]">
        {value}
        {unit && <span className="ml-1 text-base font-semibold text-[#999]">{unit}</span>}
      </p>
      <p className="text-[13px] font-semibold uppercase tracking-[1px] text-[#999]">{label}</p>
      {trend && (
        <p className={cn('mt-1.5 text-[11px] font-semibold', trendConfig[trend].color)}>
          {trendConfig[trend].icon}
          {trendPercent != null && (
            <span className="ml-0.5">
              {trendPercent > 0 ? '+' : ''}
              {trendPercent}%
            </span>
          )}
        </p>
      )}
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel}
        className={cn('block w-full appearance-none', surfaceClasses)}
      >
        {content}
      </button>
    );
  }

  // aria-label deliberately omitted: it's a no-op on a div without an ARIA role.
  return <div className={surfaceClasses}>{content}</div>;
}
